import { Stack } from '@mui/material'
import { MouseEvent, useCallback, useState } from 'react'
import { IconButton } from 'utoniq-ui'

import {
  Maybe,
  MissionsConnectionFragment,
  MissionType,
  UserStepMissionsConnectionFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head, SideMenu, SideMenuProps } from '@/components/layouts'
import {
  MissionBox,
  MissionBoxSkeleton,
  StepMissionBox,
} from '@/components/organisms'
import { ScrollFetcher, Tabs } from '@/components/organisms/functionals'
import { ArtistThemeProvider } from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  loading: boolean
  fetching: boolean
  artistName: string
  artistId: string
  hasNextPage: boolean
  hasNextPageArtistStepMission: boolean
  missions: Maybe<MissionsConnectionFragment>
  followArtists: Maybe<SideMenuProps['followArtists']>
  memberArtists: Maybe<SideMenuProps['memberArtists']>
  user: Maybe<SideMenuProps['user']>
  isArtist: Maybe<SideMenuProps['isArtist']>
  liveStreamId: string | undefined
  artistStepMissions: Maybe<UserStepMissionsConnectionFragment>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFilterArtistMission: (
    missionTypes: Maybe<Array<MissionType> | MissionType>
  ) => Promise<void>
  onFetchMoreArtistMissions: (
    missionTypes: Maybe<Array<MissionType> | MissionType>
  ) => Promise<void>
  onGetStepMissionReward: (stepMissionId: string) => Promise<void>
  onFetchMoreStepMission: () => Promise<void>
}

type MissionsCellProps = Pick<State, 'missions'> & {
  loading: boolean
  hasNextPage: boolean
  artistId: string
  onFetchMore: () => Promise<void>
}

type StepMissionsCellProps = Pick<State, 'artistStepMissions'> & {
  loading: boolean
  hasNextPage: boolean
  onFetchMore: () => Promise<void>
  onGetStepMissionReward: (stepMissionId: string) => Promise<void>
}

type FilterItemProps = {
  text: string
  selectedIndex: number | null
  onClick: (event: MouseEvent<HTMLElement>, index: number) => void
  index: number
}

export const ArtistMissionsTemplate: Template<State, Action> = ({
  state,
  // onFilterArtistMission,
  onFetchMoreArtistMissions,
  onGetStepMissionReward,
  onFetchMoreStepMission,
}) => {
  const { t } = useTranslation('artistMissions')
  const { routerNames } = useRouter()
  const [openSideMenu, setOpenSideMenu] = useState<boolean>(false)
  const [drawerIsOpen, setDrawerIsOpen] = useState<boolean>(false)
  const [currentTab, setCurrentTab] = useState<number>(0)

  const handleFetchMore = async () => {
    await onFetchMoreArtistMissions([
      MissionType.Quiz,
      MissionType.ItemCollect,
      /**
       * @description
       * twitter機能開放時に追加
       * https://app.clickup.com/t/908127/UT-3968
       *
       * MissionType.TwitterRetweet,
       * MissionType.TwitterFollow
       */
    ])
  }

  const handleTabChange = useCallback((value: number) => {
    setCurrentTab(value)
  }, [])

  return (
    <ArtistThemeProvider>
      <BaseLayout
        navigation={{
          type: 'artist',
          props: {
            isLive: !!state.liveStreamId,
            hasManagementTask: !!state.user?.hasManagementTask,
            unreadNotificationCount: state.user?.unreadNotificationCount,
          },
        }}
        header={{
          title: t('title'),
          left: config.applicationType === 'organization' && (
            <IconButton.AvatarNoImage
              edge="start"
              onClick={() => setOpenSideMenu(!openSideMenu)}
              color="icon.line"
              bgcolor="icon.background"
              badge={
                state.user?.hasManagementTask ||
                !!state.user?.unreadNotificationCount
              }
            />
          ),
        }}
      >
        <Head title={[routerNames.ArtistMissions, state.artistName]} />
        {/**
         * @description
         * diicaのみミッション一覧への動線追加
         * https://app.clickup.com/t/908127/UT-9852
         */}
        {(config.applicationKey === 'diica' ||
          config.environment !== 'production') &&
        state.user ? (
          <Tabs
            labels={[t('event'), t('accomplishment')]}
            value={currentTab}
            disableAnimateTransitions
            onChange={handleTabChange}
          >
            <MissionsCell
              artistId={state.artistId}
              missions={state.missions}
              loading={state.fetching}
              hasNextPage={state.hasNextPage}
              onFetchMore={handleFetchMore}
            />
            <StepMissionsCell
              artistStepMissions={state.artistStepMissions}
              loading={state.fetching}
              hasNextPage={state.hasNextPageArtistStepMission}
              onGetStepMissionReward={onGetStepMissionReward}
              onFetchMore={onFetchMoreStepMission}
            />
          </Tabs>
        ) : (
          <MissionsCell
            missions={state.missions}
            artistId={state.artistId}
            loading={state.fetching}
            hasNextPage={state.hasNextPage}
            onFetchMore={handleFetchMore}
          />
        )}
        <SideMenu
          followArtists={state.followArtists}
          memberArtists={state.memberArtists}
          open={openSideMenu}
          onClose={() => setOpenSideMenu(false)}
          user={state.user}
          isArtist={state.isArtist}
        />
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

const MissionsCell: Cell<MissionsCellProps> = ({
  artistId,
  missions,
  loading,
  hasNextPage,
  onFetchMore,
}) => {
  const { t } = useTranslation('artistMissions')
  return (
    <CellFrame sx={{ px: 5, pt: 5 }}>
      <ScrollFetcher
        loading={loading}
        onFetchMore={onFetchMore}
        hasNextPage={hasNextPage}
        loadingComponent={
          <Stack spacing={6} sx={{ py: 4 }}>
            {Array.from(Array(5).keys()).map(i => (
              <MissionBoxSkeleton fullWidth key={i} />
            ))}
          </Stack>
        }
        nodes={missions?.edges.map(({ node }) => (
          <MissionBox
            key={node.id}
            fullWidth
            mission={node}
            artistId={artistId}
          />
        ))}
        emptyMessage={t('missionEmptyMessage')}
      />
    </CellFrame>
  )
}

const StepMissionsCell: Cell<StepMissionsCellProps> = ({
  artistStepMissions,
  loading,
  hasNextPage,
  onFetchMore,
  onGetStepMissionReward,
}) => {
  const { t } = useTranslation('artistMissions')
  return (
    <CellFrame sx={{ px: 5 }}>
      <ScrollFetcher
        loading={loading}
        onFetchMore={onFetchMore}
        hasNextPage={hasNextPage}
        loadingComponent={
          <Stack spacing={6} sx={{ py: 4 }}>
            {Array.from(Array(5).keys()).map(i => (
              <MissionBoxSkeleton fullWidth key={i} />
            ))}
          </Stack>
        }
        nodes={artistStepMissions?.edges.map(({ node }) => {
          return (
            <StepMissionBox
              key={node.id}
              mission={node}
              onGetStepMissionReward={onGetStepMissionReward}
            />
          )
        })}
        emptyMessage={t('missionEmptyMessage')}
      />
    </CellFrame>
  )
}
